import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { BoardPortalService } from '../../services/board-portal.service';
import { BoardPortalApiActions, BoardPortalMutationActions, BoardPortalPageActions } from './board-portal.actions';

const errorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

@Injectable()
export class BoardPortalEffects {
  private readonly actions$ = inject(Actions);
  private readonly boardPortalService = inject(BoardPortalService);

  readonly loadDocuments$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalPageActions.opened),
    switchMap(() => this.boardPortalService.getAllDocuments().pipe(
      map((documents) => BoardPortalApiActions.documentsLoadSucceeded({ documents })),
      catchError((error: unknown) => of(BoardPortalApiActions.documentsLoadFailed({
        error: errorMessage(error, 'Unable to load board documents'),
      }))),
    )),
  ));

  readonly loadContacts$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalPageActions.opened),
    switchMap(() => this.boardPortalService.getAllContacts().pipe(
      map((contacts) => BoardPortalApiActions.contactsLoadSucceeded({ contacts })),
      catchError((error: unknown) => of(BoardPortalApiActions.contactsLoadFailed({
        error: errorMessage(error, 'Unable to load board contacts'),
      }))),
    )),
  ));

  readonly loadAnnouncements$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalPageActions.opened),
    switchMap(() => this.boardPortalService.getAllAnnouncements().pipe(
      map((announcements) => BoardPortalApiActions.announcementsLoadSucceeded({ announcements })),
      catchError((error: unknown) => of(BoardPortalApiActions.announcementsLoadFailed({
        error: errorMessage(error, 'Unable to load board announcements'),
      }))),
    )),
  ));

  readonly loadToolkitSections$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalPageActions.opened),
    switchMap(() => this.boardPortalService.getAllToolkitSections().pipe(
      map((toolkitSections) => BoardPortalApiActions.toolkitSectionsLoadSucceeded({ toolkitSections })),
      catchError((error: unknown) => of(BoardPortalApiActions.toolkitSectionsLoadFailed({
        error: errorMessage(error, 'Unable to load board toolkit sections'),
      }))),
    )),
  ));

  readonly createDocument$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalMutationActions.createDocumentRequested),
    switchMap(({ requestId, request }) => this.boardPortalService.createDocument(request).pipe(
      map((document) => BoardPortalMutationActions.createDocumentSucceeded({ requestId, document })),
      catchError((error: unknown) => of(BoardPortalMutationActions.createDocumentFailed({ requestId, error: errorMessage(error, 'Unable to create board document') }))),
    )),
  ));

  readonly updateDocument$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalMutationActions.updateDocumentRequested),
    switchMap(({ requestId, id, request }) => this.boardPortalService.updateDocument(id, request).pipe(
      map((document) => BoardPortalMutationActions.updateDocumentSucceeded({ requestId, document })),
      catchError((error: unknown) => of(BoardPortalMutationActions.updateDocumentFailed({ requestId, error: errorMessage(error, 'Unable to update board document') }))),
    )),
  ));

  readonly deleteDocument$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalMutationActions.deleteDocumentRequested),
    switchMap(({ requestId, id }) => this.boardPortalService.deleteDocument(id).pipe(
      map(() => BoardPortalMutationActions.deleteDocumentSucceeded({ requestId, id })),
      catchError((error: unknown) => of(BoardPortalMutationActions.deleteDocumentFailed({ requestId, error: errorMessage(error, 'Unable to delete board document') }))),
    )),
  ));

  readonly createContact$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalMutationActions.createContactRequested),
    switchMap(({ requestId, request }) => this.boardPortalService.createContact(request).pipe(
      map((contact) => BoardPortalMutationActions.createContactSucceeded({ requestId, contact })),
      catchError((error: unknown) => of(BoardPortalMutationActions.createContactFailed({ requestId, error: errorMessage(error, 'Unable to create board contact') }))),
    )),
  ));

  readonly updateContact$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalMutationActions.updateContactRequested),
    switchMap(({ requestId, id, request }) => this.boardPortalService.updateContact(id, request).pipe(
      map((contact) => BoardPortalMutationActions.updateContactSucceeded({ requestId, contact })),
      catchError((error: unknown) => of(BoardPortalMutationActions.updateContactFailed({ requestId, error: errorMessage(error, 'Unable to update board contact') }))),
    )),
  ));

  readonly deleteContact$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalMutationActions.deleteContactRequested),
    switchMap(({ requestId, id }) => this.boardPortalService.deleteContact(id).pipe(
      map(() => BoardPortalMutationActions.deleteContactSucceeded({ requestId, id })),
      catchError((error: unknown) => of(BoardPortalMutationActions.deleteContactFailed({ requestId, error: errorMessage(error, 'Unable to delete board contact') }))),
    )),
  ));

  readonly createAnnouncement$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalMutationActions.createAnnouncementRequested),
    switchMap(({ requestId, request }) => this.boardPortalService.createAnnouncement(request).pipe(
      map((announcement) => BoardPortalMutationActions.createAnnouncementSucceeded({ requestId, announcement })),
      catchError((error: unknown) => of(BoardPortalMutationActions.createAnnouncementFailed({ requestId, error: errorMessage(error, 'Unable to create announcement') }))),
    )),
  ));

  readonly updateAnnouncement$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalMutationActions.updateAnnouncementRequested),
    switchMap(({ requestId, id, request }) => this.boardPortalService.updateAnnouncement(id, request).pipe(
      map((announcement) => BoardPortalMutationActions.updateAnnouncementSucceeded({ requestId, announcement })),
      catchError((error: unknown) => of(BoardPortalMutationActions.updateAnnouncementFailed({ requestId, error: errorMessage(error, 'Unable to update announcement') }))),
    )),
  ));

  readonly deleteAnnouncement$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalMutationActions.deleteAnnouncementRequested),
    switchMap(({ requestId, id }) => this.boardPortalService.deleteAnnouncement(id).pipe(
      map(() => BoardPortalMutationActions.deleteAnnouncementSucceeded({ requestId, id })),
      catchError((error: unknown) => of(BoardPortalMutationActions.deleteAnnouncementFailed({ requestId, error: errorMessage(error, 'Unable to delete announcement') }))),
    )),
  ));

  readonly createToolkitSection$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalMutationActions.createToolkitSectionRequested),
    switchMap(({ requestId, request }) => this.boardPortalService.createToolkitSection(request).pipe(
      map((section) => BoardPortalMutationActions.createToolkitSectionSucceeded({ requestId, section })),
      catchError((error: unknown) => of(BoardPortalMutationActions.createToolkitSectionFailed({ requestId, error: errorMessage(error, 'Unable to create toolkit section') }))),
    )),
  ));

  readonly updateToolkitSection$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalMutationActions.updateToolkitSectionRequested),
    switchMap(({ requestId, id, request }) => this.boardPortalService.updateToolkitSection(id, request).pipe(
      map((section) => BoardPortalMutationActions.updateToolkitSectionSucceeded({ requestId, section })),
      catchError((error: unknown) => of(BoardPortalMutationActions.updateToolkitSectionFailed({ requestId, error: errorMessage(error, 'Unable to update toolkit section') }))),
    )),
  ));

  readonly deleteToolkitSection$ = createEffect(() => this.actions$.pipe(
    ofType(BoardPortalMutationActions.deleteToolkitSectionRequested),
    switchMap(({ requestId, id }) => this.boardPortalService.deleteToolkitSection(id).pipe(
      map(() => BoardPortalMutationActions.deleteToolkitSectionSucceeded({ requestId, id })),
      catchError((error: unknown) => of(BoardPortalMutationActions.deleteToolkitSectionFailed({ requestId, error: errorMessage(error, 'Unable to delete toolkit section') }))),
    )),
  ));
}